import { useQuery, useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Helmet } from 'react-helmet-async'
import { getCanonicalUrl } from '../utils/canonical'
import { 
  Save, 
  Key, 
  Bell, 
  Shield,
  Copy,
  RefreshCw
} from 'lucide-react'
import { getSettings, updateSettings } from '../services/api'

export default function SettingsPage() {
  const { data: settings, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  })

  const { register, handleSubmit } = useForm({
    defaultValues: settings,
  })

  const updateMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      toast.success('Settings updated successfully')
      refetch()
    },
    onError: () => {
      toast.error('Failed to update settings')
    },
  })

  const onSubmit = (data: any) => {
    updateMutation.mutate(data)
  }

  const copyApiKey = () => {
    if (settings?.apiKey) {
      navigator.clipboard.writeText(settings.apiKey)
      toast.success('API key copied to clipboard')
    }
  }

  const regenerateApiKey = () => {
    // This would call an API to regenerate the key
    toast.success('API key regenerated')
    refetch()
  }

  return (
    <>
      <Helmet>
        <title>Settings - Accessibility Checker</title>
        <meta name="description" content="Manage your accessibility checker account settings and preferences" />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={getCanonicalUrl('/settings')} />
      </Helmet>
      <div className="max-w-3xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Configure your accessibility testing preferences</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* API Key Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Key className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">API Access</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={settings?.apiKey || 'No API key generated'}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={copyApiKey}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={regenerateApiKey}
                  className="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Use this API key to integrate accessibility testing into your CI/CD pipeline
              </p>
            </div>
          </div>
        </div>

        {/* Default Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Default Test Settings</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default WCAG Level
              </label>
              <select
                {...register('defaultWcagLevel')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="A">Level A</option>
                <option value="AA">Level AA (Recommended)</option>
                <option value="AAA">Level AAA</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Rules
              </label>
              <textarea
                {...register('customRules')}
                placeholder="Enter custom rule IDs, one per line"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              <p className="mt-2 text-sm text-gray-500">
                Add custom accessibility rules to include in all tests
              </p>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
          </div>
          
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register('notifications')}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Email notifications for completed scans
              </span>
            </label>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={updateMutation.isLoading}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            Save Settings
          </button>
        </div>
      </form>
    </div>
    </>
  )
}
